import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AppLayout from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Users, Plus, Trash2, Loader2, UsersRound, UserPlus, UserMinus, Eye } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { apiClient } from "@/lib/api/client";

interface Cohort {
  id: string;
  name: string;
  teacher: string;
  teacher_name: string;
  year_group: string;
  academic_year: string;
  student_count: number;
  is_active: boolean;
  created_at: string;
}

interface Student {
  id: string;
  student_id: string;
  full_name: string;
  email: string;
  year_group: string;
  eal: boolean;
}

interface CreateCohortData {
  name: string;
  teacher_id: string;
  year_group: string;
  academic_year: string;
}

const yearGroups = ["Reception", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13"];
const academicYears = ["2024-2025", "2025-2026", "2026-2027"];

export default function AdminCohortsManagementPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isManageStudentsOpen, setIsManageStudentsOpen] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState<Cohort | null>(null);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const [formData, setFormData] = useState<CreateCohortData>({
    name: "", teacher_id: "", year_group: "", academic_year: "2025-2026",
  });

  const { data: cohorts, isLoading } = useQuery({
    queryKey: ["adminCohorts"],
    queryFn: async () => {
      const response = await apiClient.get<Cohort[] | { results: Cohort[] }>("/students/cohorts/");
      const data = response.data;
      return Array.isArray(data) ? data : (data as { results: Cohort[] }).results;
    },
  });

  const { data: teachers } = useQuery({
    queryKey: ["adminTeachers"],
    queryFn: async () => {
      const response = await apiClient.get<{ id: string; full_name: string }[] | { results: { id: string; full_name: string }[] }>("/auth/users/?role=teacher");
      const data = response.data;
      return Array.isArray(data) ? data : (data as { results: { id: string; full_name: string }[] }).results;
    },
  });

  const { data: allStudents } = useQuery({
    queryKey: ["adminStudents"],
    queryFn: async () => {
      const response = await apiClient.get<Student[] | { results: Student[] }>("/students/");
      const data = response.data;
      return Array.isArray(data) ? data : (data as { results: Student[] }).results;
    },
  });

  const { data: studentsInCohort, isLoading: loadingCohortStudents } = useQuery({
    queryKey: ["studentsInCohort", selectedCohort?.id],
    queryFn: async () => {
      if (!selectedCohort) return [];
      const response = await apiClient.get<Student[] | { results: Student[] }>(`/students/?cohort=${selectedCohort.id}`);
      const data = response.data;
      return Array.isArray(data) ? data : (data as { results: Student[] }).results;
    },
    enabled: !!selectedCohort,
  });

  const { data: studentsNotInCohort, isLoading: loadingAvailableStudents } = useQuery({
    queryKey: ["studentsNotInCohort", selectedCohort?.id],
    queryFn: async () => {
      if (!selectedCohort) return [];
      const response = await apiClient.get<Student[] | { results: Student[] }>(`/students/cohorts/${selectedCohort.id}/available_students/`);
      const data = response.data;
      return Array.isArray(data) ? data : (data as { results: Student[] }).results;
    },
    enabled: !!selectedCohort,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateCohortData) => {
      const response = await apiClient.post<Cohort>("/students/cohorts/", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminCohorts"] });
      toast({ title: "Success", description: "Cohort created successfully" });
      handleCloseCreateDialog();
    },
    onError: (error: Error) => {
      toast({ variant: "destructive", title: "Error", description: error.message || "Failed to create cohort" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/students/cohorts/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminCohorts"] });
      toast({ title: "Success", description: "Cohort deleted successfully" });
    },
    onError: (error: Error) => {
      toast({ variant: "destructive", title: "Error", description: error.message || "Failed to delete cohort" });
    },
  });

  const addStudentMutation = useMutation({
    mutationFn: async ({ cohortId, studentId }: { cohortId: string; studentId: string }) => {
      await apiClient.post(`/students/cohorts/${cohortId}/add_student/`, { student_id: studentId });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["studentsInCohort", variables.cohortId] });
      queryClient.invalidateQueries({ queryKey: ["studentsNotInCohort", variables.cohortId] });
      queryClient.invalidateQueries({ queryKey: ["adminCohorts"] });
      toast({ title: "Success", description: "Student added to cohort" });
    },
    onError: (error: Error) => {
      toast({ variant: "destructive", title: "Error", description: error.message || "Failed to add student" });
    },
  });

  const removeStudentMutation = useMutation({
    mutationFn: async ({ cohortId, studentId }: { cohortId: string; studentId: string }) => {
      await apiClient.post(`/students/cohorts/${cohortId}/remove_student/`, { student_id: studentId });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["studentsInCohort", variables.cohortId] });
      queryClient.invalidateQueries({ queryKey: ["studentsNotInCohort", variables.cohortId] });
      queryClient.invalidateQueries({ queryKey: ["adminCohorts"] });
      toast({ title: "Success", description: "Student removed from cohort" });
    },
    onError: (error: Error) => {
      toast({ variant: "destructive", title: "Error", description: error.message || "Failed to remove student" });
    },
  });

  const filteredCohorts = (cohorts || []).filter(
    (cohort) =>
      cohort.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cohort.teacher_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cohort.year_group?.includes(searchTerm)
  );

  const handleOpenCreateDialog = () => {
    setFormData({ name: "", teacher_id: "", year_group: "", academic_year: "2025-2026" });
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleDelete = (id: string) => {
    if (!confirm("Are you sure you want to delete this cohort?")) return;
    deleteMutation.mutate(id);
  };

  const handleManageStudents = (cohort: Cohort) => {
    setSelectedCohort(cohort);
    setStudentSearchTerm("");
    setIsManageStudentsOpen(true);
  };

  const handleAddStudent = (studentId: string) => {
    if (selectedCohort) {
      addStudentMutation.mutate({ cohortId: selectedCohort.id, studentId });
    }
  };

  const handleRemoveStudent = (studentId: string) => {
    if (selectedCohort) {
      removeStudentMutation.mutate({ cohortId: selectedCohort.id, studentId });
    }
  };

  const filteredStudentsInCohort = (studentsInCohort || []).filter(
    (s) => s.full_name.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
      s.student_id.toLowerCase().includes(studentSearchTerm.toLowerCase())
  );
  
  const filteredStudentsNotInCohort = (studentsNotInCohort || []).filter(
    (s) => s.full_name.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
      s.student_id.toLowerCase().includes(studentSearchTerm.toLowerCase())
  );

  return (
    <AppLayout role="admin">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Cohorts Management</h1>
            <p className="text-muted-foreground">Manage student cohorts</p>
          </div>
          <Button onClick={handleOpenCreateDialog}><Plus className="w-4 h-4 mr-2" />Add Cohort</Button>
        </div>

        <div className="flex gap-4">
          <div className="flex-1"><Input placeholder="Search cohorts..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="max-w-sm" /></div>
        </div>

        <Card>
          <CardHeader><CardTitle>Cohorts</CardTitle><CardDescription>All registered cohorts</CardDescription></CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
            ) : filteredCohorts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground"><UsersRound className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No cohorts found</p></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Year Group</TableHead>
                    <TableHead>Academic Year</TableHead>
                    <TableHead>Teacher</TableHead>
                    <TableHead>Students</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCohorts.map((cohort) => (
                    <TableRow key={cohort.id}>
                      <TableCell className="font-medium">{cohort.name}</TableCell>
                      <TableCell>{cohort.year_group || "—"}</TableCell>
                      <TableCell>{cohort.academic_year || "—"}</TableCell>
                      <TableCell>{cohort.teacher_name || "—"}</TableCell>
                      <TableCell>{cohort.student_count ?? 0}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${cohort.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                          {cohort.is_active ? "Active" : "Inactive"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleManageStudents(cohort)} title="Manage Students">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(cohort.id)} disabled={deleteMutation.isPending}>
                            {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 text-destructive" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Cohort Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Cohort</DialogTitle>
              <DialogDescription>Create a new student cohort</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Cohort Name</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="e.g., Year 5B" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="year_group">Year Group</Label>
                  <Select value={formData.year_group} onValueChange={(value) => setFormData({ ...formData, year_group: value })}>
                    <SelectTrigger><SelectValue placeholder="Select year group" /></SelectTrigger>
                    <SelectContent>
                      {yearGroups.map((yg) => (<SelectItem key={yg} value={yg}>Year {yg}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="academic_year">Academic Year</Label>
                  <Select value={formData.academic_year} onValueChange={(value) => setFormData({ ...formData, academic_year: value })}>
                    <SelectTrigger><SelectValue placeholder="Select academic year" /></SelectTrigger>
                    <SelectContent>
                      {academicYears.map((ay) => (<SelectItem key={ay} value={ay}>{ay}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="teacher">Assigned Teacher</Label>
                  <Select value={formData.teacher_id} onValueChange={(value) => setFormData({ ...formData, teacher_id: value })}>
                    <SelectTrigger><SelectValue placeholder="Select teacher" /></SelectTrigger>
                    <SelectContent>
                      {teachers?.map((teacher) => (<SelectItem key={teacher.id} value={teacher.id}>{teacher.full_name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseCreateDialog} disabled={createMutation.isPending}>Cancel</Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Manage Students Dialog */}
        <Dialog open={isManageStudentsOpen} onOpenChange={setIsManageStudentsOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Manage Students - {selectedCohort?.name}</DialogTitle>
              <DialogDescription>Add or remove students from this cohort</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <Input 
                placeholder="Search students..." 
                value={studentSearchTerm} 
                onChange={(e) => setStudentSearchTerm(e.target.value)} 
              />
              
              <div className="grid grid-cols-2 gap-4">
                {/* Current Students */}
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Current Students ({filteredStudentsInCohort.length})
                  </h4>
                  <div className="border rounded-lg max-h-60 overflow-y-auto">
                    {loadingCohortStudents ? (
                      <div className="p-4 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                    ) : filteredStudentsInCohort.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground text-sm">No students in this cohort</div>
                    ) : (
                      <div className="divide-y">
                        {filteredStudentsInCohort.map((student) => (
                          <div key={student.id} className="flex items-center justify-between p-2">
                            <div>
                              <p className="text-sm font-medium">{student.full_name}</p>
                              <p className="text-xs text-muted-foreground">{student.student_id}</p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleRemoveStudent(student.id)}
                              disabled={removeStudentMutation.isPending}
                            >
                              <UserMinus className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Available Students */}
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <UserPlus className="w-4 h-4" /> Available Students ({filteredStudentsNotInCohort.length})
                  </h4>
                  <div className="border rounded-lg max-h-60 overflow-y-auto">
                    {loadingAvailableStudents ? (
                      <div className="p-4 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                    ) : filteredStudentsNotInCohort.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground text-sm">No available students</div>
                    ) : (
                      <div className="divide-y">
                        {filteredStudentsNotInCohort.slice(0, 20).map((student) => (
                          <div key={student.id} className="flex items-center justify-between p-2">
                            <div>
                              <p className="text-sm font-medium">{student.full_name}</p>
                              <p className="text-xs text-muted-foreground">{student.student_id} - Year {student.year_group}</p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleAddStudent(student.id)}
                              disabled={addStudentMutation.isPending}
                            >
                              <UserPlus className="w-4 h-4 text-green-600" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsManageStudentsOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}